import * as React from "react";
import { useMappedState } from "redux-react-hook";
import _ from "lodash";

import { getOrderLinesByRelatedGroup } from "src/core/orders/utils/orders";
import type { IStore } from "src/core/shared/store";
import { IOrderLine } from "src/core/orders/api/orders.api";
import { ErrorLevel } from "src/core/products/api/products.api";
import { IListLine } from "src/core/lists/api/lists.api";

/**
 * Check if the order passes validation specifically against product related groups
 * @return {error: string | null; level: ErrorLevel | null;}
 */
const useValidAgainstProductRelatedGroups = () => {
  const { productRelatedGroups } = useMappedState(({ global }: IStore) => ({ productRelatedGroups: global.settings?.productRelatedGroups || null }));

  const validAgainstProductRelatedGroups = React.useCallback(
    (orderOrListLines: IOrderLine[] | IListLine[]): {
      message: string | null;
      errorLevel?: ErrorLevel;
    } => {
      // check related group logic
      if (productRelatedGroups) {
        const { orderLinesGroupedByParent, childrenWithNoParent } = getOrderLinesByRelatedGroup(orderOrListLines, productRelatedGroups);
        const defaultErrorMessage = "Order contains an invalid mix of products. Please speak to an administrator.";

        // handle children with no parent
        if (childrenWithNoParent && childrenWithNoParent.length > 0) {
          // grab all the relevant related groups
          const relatedGroupIds = _.uniq(_.compact(childrenWithNoParent.map(child => child.product.relatedGroupId)));
          const childRelatedGroups = relatedGroupIds.map(id => productRelatedGroups.find(group => group.id === id));
          // no need to check against quantity, because we know at this point there are no parents

          if (childRelatedGroups.length) {
            // validate against each related group setting
            for (const childRelatedGroup of childRelatedGroups) {
              // find the parent related group
              const parentRelatedGroup = productRelatedGroups.find(group => group.id === childRelatedGroup?.parentId);

              if (parentRelatedGroup?.settings?.childProducts?.errorLevel === ErrorLevel.Error) {
                const message = parentRelatedGroup?.settings?.childProducts?.message;

                return {
                  message: message || defaultErrorMessage,
                  errorLevel: ErrorLevel.Error
                };
              }
            }
          }
        }

        // handle parents with children
        if (Object.keys(orderLinesGroupedByParent).length) {
          // validate against each related group setting
          for (const relatedGroupKey in orderLinesGroupedByParent) {
            // get totals
            const relatedGroupOrderLines = orderLinesGroupedByParent[ relatedGroupKey ];
            const parentTotal = relatedGroupOrderLines.parentOrderLines.reduce((acc, ol) => acc + ol.quantity, 0);
            const childTotal = relatedGroupOrderLines.childOrderLines.reduce((acc, ol) => acc + ol.quantity, 0);
            const relatedGroup = productRelatedGroups.find(group => group.id === Number(relatedGroupKey));

            if (parentTotal < childTotal) {
              const message = relatedGroup?.settings?.childProducts?.message;

              return {
                message: message || null,
                errorLevel: relatedGroup?.settings?.childProducts?.errorLevel
              };
            }

            if (parentTotal > childTotal) {
              const message = relatedGroup?.settings?.parentProducts?.message;

              return {
                message: message || null,
                errorLevel: relatedGroup?.settings?.parentProducts?.errorLevel
              };
            }
          }
        }
      }

      return { message: null };
    },
    [ productRelatedGroups ]
  );

  return validAgainstProductRelatedGroups;
};

export default useValidAgainstProductRelatedGroups;
